import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { atomWithDebounce } from '@/atoms/debounce';
import { playerMetricsAtom } from '@/atoms/metric/playerMetrics';
import { comparisonIdAtom } from '@/atoms/queryParams';
import { selectedRadarTemplateIncludeParamsAtom } from '@/atoms/radar';
import { playerNormalisedAggregatesUrl } from '@/query/url';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { hasEmptyInFilters } from '@/utils/api';
import { unwrapWithLoading } from '@/utils/jotai';
import { fetchClientAtom } from '../client';
import { allPlayersOptionsAtom } from '../players';
import { radarDataAtom } from '../radar';
import { playerRadarTableColumnsAtom } from '../tableColumns';
import { playerRadarTemplatesAtom } from '../userConfigs';

export const playerComparisonRadarFilterDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
export const { debouncedValueAtom: playerComparisonRadarFilterAtom } = playerComparisonRadarFilterDebounceObject;

const rawPlayerComparisonRadarDataAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const comparisonId = get(comparisonIdAtom);
  const filterParams = get(playerComparisonRadarFilterAtom);
  const includeParams = get(selectedRadarTemplateIncludeParamsAtom);

  const queryKey = ['playerComparisonRadarData', comparisonId, filterParams, includeParams] as const;

  const queryFn = async ({
    queryKey: [, comparisonId, filterParams, includeParams],
  }: {
    queryKey: typeof queryKey;
  }) => {
    const url = playerNormalisedAggregatesUrl(comparisonId, { ...filterParams, ...includeParams });
    if (!url || hasEmptyInFilters(filterParams)) return {};

    return (await fetch(url)) as Record<string, number>;
  };

  return { queryKey, queryFn };
});

export const playerComparisonRadarDataAtom = unwrapWithLoading(
  radarDataAtom(
    rawPlayerComparisonRadarDataAtom,
    playerMetricsAtom,
    playerRadarTemplatesAtom,
    playerRadarTableColumnsAtom,
  ),
  [],
);

export const playerComparisonIsLoadingAtom = atom(get => {
  const comparisonId = get(comparisonIdAtom);
  if (!comparisonId) return false;
  const { isLoading: radarIsLoading } = get(playerComparisonRadarDataAtom);
  return radarIsLoading;
});

export const playerComparisonMinutesPlayedAtom = atom(async (get): Promise<number | undefined> => {
  const rawPlayerComparisonRadarData = (await get(rawPlayerComparisonRadarDataAtom)).data;
  return rawPlayerComparisonRadarData.minutes_played;
});

export const playerComparisonMinutesPlayedWithFallbackAtom = atom(async get => {
  const minutesPlayed = await get(playerComparisonMinutesPlayedAtom);
  return minutesPlayed || 0;
});

export const unwrappedPlayerComparisonMinutesPlayedAtom = unwrap(
  playerComparisonMinutesPlayedWithFallbackAtom,
  prev => prev || 0,
);

const playerComparisonMinutesPlayed360Atom = atom(async get => {
  const rawPlayerComparisonRadarData = (await get(rawPlayerComparisonRadarDataAtom)).data;
  return rawPlayerComparisonRadarData.minutes_played_360 || 0;
});

export const unwrappedPlayerComparisonMinutesPlayed360Atom = unwrap(
  playerComparisonMinutesPlayed360Atom,
  prev => prev || 0,
);

const playerComparisonNameAtom = atom(async get => {
  const comparisonId = get(comparisonIdAtom);
  if (!comparisonId) return '';
  const options = await get(allPlayersOptionsAtom);
  const matchingOption = options.find(option => option.value === comparisonId);
  return matchingOption?.optional?.nickname || matchingOption?.label || '';
});
export const unwrappedPlayerComparisonNameAtom = unwrap(playerComparisonNameAtom, prev => prev || '');
